import { compact, flattenDeep, isArray, isNumber, isString } from "lodash";
import { BooleanFilter, ICellData } from "@treelab/common-logic";

import { IFilterMeta, IAdaptedFilterCtrl, OperatorType } from "../types/form-type";
import { ColumnType, ITableData, ITableRowData } from "../types/table-type";
import { IBasicData } from "../types/global-type";

import { arrayToObject, getCollaborators } from "../utils/tableData_secondary";
import { getRealColumn, getOriginColumn } from "../utils/option_main";

import { ROLLUP_NUMBER_SPLIT_KEY, IS_FORMULA_COLUMN_TYPE } from "../constants/tableData";
import { checkValueInArr } from "../utils/form_secondary";
import { FILTER_TYPE_MAP } from "../utils/filter_main";
import { CONDITION_OPTIONS } from "../utils/filter_secondary";

interface IProps {
  tableData: ITableData;
  filterMeta: IFilterMeta;
}

class ConvertTableData {
  private tableData: ITableData;
  private filterMeta: IFilterMeta;

  constructor(props: IProps) {
    const { tableData, filterMeta } = props;
    this.tableData = tableData;
    this.filterMeta = filterMeta;
  }

  // 计算 原始数据 包括筛选 暂不拆分维度array 直接拆分value数组
  getBasicDataByFilter(
    tableData: ITableData,
    getDataForRow: (row: ITableRowData) => IBasicData | IBasicData[],
    isSplitValue: boolean = true
  ): IBasicData[] {
    const { columns, rows } = tableData;
    const { filterValues, operatorType } = this.filterMeta || {
      filterValues: [],
      operatorType: OperatorType.AND,
    };
    const filters = filterValues
      .filter((it) => {
        const column = columns.find((o) => o.id === it.columnId);
        return column && FILTER_TYPE_MAP[column.type] === it.filterType;
      })
      .map((item) => ({
        ...item,
        operatorType,
      })) as IAdaptedFilterCtrl[];
    const columnObj = arrayToObject(columns, "id");
    const { collaboratorsById } = getCollaborators();
    const booleanFilter = new BooleanFilter();
    booleanFilter.setModel(filters, columnObj, collaboratorsById);
    const dataList = rows.map((item) => {
      const cellValue = item.cells;
      if (booleanFilter.doesFilterPass({ data: cellValue as ICellData })) {
        return getDataForRow(item);
      } else {
        return null;
      }
    });
    const resultList = compact(dataList);
    return flattenDeep(resultList);
  }

  // 按指定 key 拆分
  splitArrayValueByKey(list: IBasicData[], key: string): IBasicData[] {
    if (!isString(key) || key.length === 0) return list;
    const isRollupNumber = this.checkRollupNumber(key);
    const tempList = list.map((item) => {
      let target = item[key];
      if (isRollupNumber) {
        if (isString(target) && target.includes(ROLLUP_NUMBER_SPLIT_KEY)) {
          target = target
            .split(ROLLUP_NUMBER_SPLIT_KEY)
            .map((item) => (isNumber(item) ? item : Number.parseFloat(item)));
        }
      }
      if (!Array.isArray(target)) return item;
      if (target.length === 0) return { ...item, [key]: undefined };
      const result = target.map((item2) => ({
        ...item,
        [key]: item2,
      }));
      return result;
    });
    const resultList = compact(tempList);
    return flattenDeep(resultList);
  }

  // 判断当前列 是否为 rollup 一个 number 类型列   是则需要拆分 string
  checkRollupNumber(key?: string) {
    if (!key) return false;
    const realType = getRealColumn(key, this.tableData)?.type;
    const originalType = getOriginColumn(this.tableData, key)?.type;
    if (!!originalType && originalType === ColumnType.ROLLUP) {
      if (!!realType && checkValueInArr(realType, [ColumnType.NUMBER, ColumnType.RATING, ColumnType.CURRENCY])) {
        return true;
      }
    }
    return false;
  }

  // 是否为拼接字符串
  checkIsJoinString(key?: string) {
    if (!key) return false;
    const realType = getRealColumn(key, this.tableData)?.type;
    const originalType = getOriginColumn(this.tableData, key)?.type;
    if (!!originalType && IS_FORMULA_COLUMN_TYPE[originalType]) {
      if (
        !!realType &&
        checkValueInArr(realType, [
          ColumnType.NUMBER,
          ColumnType.RATING,
          ColumnType.CURRENCY,
          ColumnType.DATETIME,
          ColumnType.CREATED_AT,
        ])
      ) {
        return true;
      }
    }
    return false;
  }

  // 拆分拼接字符串
  splitJoinString(list: IBasicData[], key: string): IBasicData[] {
    const result = list.map((item) => {
      let value = item[key];
      if (isString(value) && value.includes(ROLLUP_NUMBER_SPLIT_KEY)) {
        value = value.split(ROLLUP_NUMBER_SPLIT_KEY);
      }
      if (isArray(value)) {
        return value.map((item2) => ({ ...item, [key]: item2 }));
      }
      return item;
    });
    return flattenDeep(result);
  }

  // 校验是否为 公式一个数字类型列 因为这样情况下 number 会被转为 string
  checkIsFormulaNumber(key?: string) {
    if (!key) return false;
    const realType = getRealColumn(key, this.tableData)?.type;
    const originalType = getOriginColumn(this.tableData, key)?.type;
    if (!!originalType && IS_FORMULA_COLUMN_TYPE[originalType]) {
      if (!!realType && checkValueInArr(realType, [ColumnType.NUMBER, ColumnType.RATING, ColumnType.CURRENCY])) {
        return true;
      }
    }
    return false;
  }

  splitValueArr(list: IBasicData[], key: string): IBasicData[] {
    const result = list.map((item) => {
      if (isArray(item[key])) {
        return item[key].map((item2: any) => ({
          ...item,
          [key]: item2,
        }));
      } else {
        return item;
      }
    });
    return flattenDeep(result);
  }

  // 对于 rollup一个number类型的列 手动拆分 v2重构时打算 后端返回原始值 前端拼接 然而没有做
  splitRollupNumber(list: IBasicData[], key: string): IBasicData[] {
    const result = list.map((item) => {
      let value = item[key];
      if (isString(value) && value.includes(ROLLUP_NUMBER_SPLIT_KEY)) {
        value = value
          .split(ROLLUP_NUMBER_SPLIT_KEY)
          .map((item2) => (isNumber(item2) ? item2 : Number.parseFloat(item2)));
      }
      if (isArray(value)) {
        return value.map((item2) => ({ ...item, [key]: item2 }));
      }
      return item;
    });
    return flattenDeep(result);
  }
}

export default ConvertTableData;
